import {
  ChangeEvent,
  ReactNode,
  SelectHTMLAttributes,
  forwardRef,
} from "react";
import { usePlosTranslations } from "../../../contexts/PlosTranslationsContext";
import ChevronDown from "../../../img/icons/chevron-down.svg?react";
import ErrorIcon from "../../../img/icons/error.svg?react";
import fieldErrorStyles from "../FieldError/FieldError.module.css";
import { FormLabel } from "../FormLabel";
import styles from "./FormSelect.module.css";

export interface MyFormSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "disabled"
> {
  error?: string;
  id: string;
  label: string;
  description?: string;
  placeholder?: string;
  isDisabled?: boolean;
  children: ReactNode;
  value: string | number;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
}

const FormSelect = forwardRef<HTMLSelectElement, MyFormSelectProps>(
  (
    {
      id,
      label,
      description,
      isDisabled,
      placeholder,
      error,
      children,
      onChange,
      value,
      ...props
    },
    ref,
  ) => {
    const { formSelect: defaults } = usePlosTranslations();
    const placeholderText = placeholder ?? defaults.placeholder;
    const styleVariant = value == "" ? "placeholder" : "default";

    return (
      <div>
        <FormLabel
          label={label}
          description={description}
          isDisabled={isDisabled}
          htmlFor={id}
        />
        <div className={styles.selectWrapper}>
          <select
            ref={ref}
            id={id}
            onChange={onChange}
            value={value}
            className={styles[styleVariant]}
            disabled={isDisabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            {...props}
          >
            <option value={""} disabled hidden>
              {placeholderText}
            </option>

            {children}
          </select>
          <div className={styles.chevronWrapper}>
            <ChevronDown />
          </div>
        </div>
        {error && (
          <p id={`${id}-error`} className={fieldErrorStyles.fieldError}>
            <ErrorIcon aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default FormSelect;
